import React, {useState, useEffect} from 'react';
import {useNavigate} from 'react-router-dom';
import {motion} from 'framer-motion';
import toast from 'react-hot-toast';
import {useOrder} from '../../contexts/OrderContext';
import {useMembership} from '../../contexts/MembershipContext';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import supabase from '../../lib/supabase';

const {FiSearch, FiStar, FiHome, FiMonitor, FiArrowLeft} = FiIcons;

const ServiceSelection = () => {
  const [selectedService, setSelectedService] = useState(null);
  const [additionalContacts, setAdditionalContacts] = useState({mobile: '', email: ''});
  const [showDetails, setShowDetails] = useState(false);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredServices, setFilteredServices] = useState([]);
  const [serviceType, setServiceType] = useState(null);
  const navigate = useNavigate();
  const {createOrder} = useOrder();
  const {canUseForApplication, getMembershipDetails} = useMembership();
  const [hasMembership, setHasMembership] = useState(false);
  const [membershipDetails, setMembershipDetails] = useState(null);

  useEffect(() => {
    // Get the selected service type from localStorage
    const selectedType = localStorage.getItem('selectedServiceType');
    if (selectedType) {
      setServiceType(selectedType);
    } else {
      // If no service type selected, redirect to service type selection
      toast.error('You must choose either Home Services or Online Services to proceed with your application.');
      navigate('/service-type-selection');
      return;
    }

    // Check if user has active membership
    const details = getMembershipDetails();
    if (details) {
      setHasMembership(true);
      setMembershipDetails(details);
    } else {
      setHasMembership(false);
    }
  }, [getMembershipDetails, navigate]);

  useEffect(() => {
    // Fetch services from Supabase with local override fallback
    const fetchServices = async () => {
      try {
        setLoading(true);
        // Try category-scoped first
        let query = supabase.from('services_dy3xr').select('*');
        let { data, error } = await query.eq('category', 'application');

        // If the column doesn't exist or returns empty, fall back to all services
        if ((error && (error.message?.includes('column') || error.code)) || !data || data.length === 0) {
          const fallback = await supabase.from('services_dy3xr').select('*');
          data = fallback.data;
        }

        if (error && !data) throw error;
        // Merge with local admin overrides if exist
        let localOverrides = [];
        try {
          const raw = localStorage.getItem('how2apply_admin_services');
          if (raw) localOverrides = JSON.parse(raw);
        } catch (e) { console.warn('Local overrides parse failed:', e); }

        if (data && data.length > 0) {
          // Filter out duplicate services by name to ensure each service is displayed only once
          const byName = new Map();
          for (const svc of data) {
            byName.set(svc.name, svc);
          }
          for (const ov of localOverrides) {
            // map Admin UI model to DB-like fields for customer view
            const merged = {
              id: ov.id,
              name: ov.name,
              category: ov.category,
              description: ov.description,
              application_fee: Number(ov.application_fee ?? ov.applicationFee ?? 0),
              service_fee: Number(ov.service_fee ?? ov.serviceFee ?? 0),
              taxes: Number(ov.taxes ?? 0),
              processing_time: ov.processing_time || '2-4 hours',
              required_documents: ov.required_documents || [],
              icon: ov.icon || 'FiFileText',
              color: ov.color || 'from-blue-500 to-blue-600',
            };
            byName.set(merged.name, merged);
          }
          const uniqueServices = Array.from(byName.values());
          
          // Transform data to include the icon component
          const servicesWithIcons = uniqueServices.map(service => {
            const IconComponent = FiIcons[service.icon] || FiIcons.FiFileText;
            return { ...service, iconComponent: IconComponent };
          });
          setServices(servicesWithIcons);
          setFilteredServices(servicesWithIcons);
        } else {
          // No DB data: rely only on local overrides if any
          if (localOverrides.length > 0) {
            const mapped = localOverrides.map(ov => ({
              id: ov.id,
              name: ov.name,
              category: ov.category,
              description: ov.description,
              application_fee: Number(ov.application_fee ?? ov.applicationFee ?? 0),
              service_fee: Number(ov.service_fee ?? ov.serviceFee ?? 0),
              taxes: Number(ov.taxes ?? 0),
              processing_time: ov.processing_time || '2-4 hours',
              required_documents: ov.required_documents || [],
              icon: ov.icon || 'FiFileText',
              color: ov.color || 'from-blue-500 to-blue-600',
              iconComponent: FiIcons[ov.icon] || FiIcons.FiFileText,
            }));
            setServices(mapped);
            setFilteredServices(mapped);
          } else {
            setServices([]);
            setFilteredServices([]);
          }
        }
      } catch (error) {
        console.error('Error fetching services:', error);
        toast.error('Failed to load services');
        // Try local fallback
        try {
          const raw = localStorage.getItem('how2apply_admin_services');
          const local = raw ? JSON.parse(raw) : [];
          const mapped = local.map(ov => ({
            id: ov.id,
            name: ov.name,
            category: ov.category,
            description: ov.description,
            application_fee: Number(ov.application_fee ?? ov.applicationFee ?? 0),
            service_fee: Number(ov.service_fee ?? ov.serviceFee ?? 0),
            taxes: Number(ov.taxes ?? 0),
            processing_time: ov.processing_time || '2-4 hours',
            required_documents: ov.required_documents || [],
            icon: ov.icon || 'FiFileText',
            color: ov.color || 'from-blue-500 to-blue-600',
            iconComponent: FiIcons[ov.icon] || FiIcons.FiFileText,
          }));
          setServices(mapped);
          setFilteredServices(mapped);
        } catch (e) {
          console.warn('Local fallback failed:', e);
          setServices([]);
          setFilteredServices([]);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchServices();

    // Real-time subscription without category filter for compatibility
    const subscription = supabase
      .channel('services_changes')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'services_dy3xr' }, () => fetchServices())
      .subscribe();

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  useEffect(() => {
    // Filter services based on search query
    if (searchQuery.trim() === '') {
      setFilteredServices(services);
    } else {
      const filtered = services.filter(service =>
        service.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        service.description.toLowerCase().includes(searchQuery.toLowerCase())
      );
      setFilteredServices(filtered);
    }
  }, [searchQuery, services]);

  const handleServiceSelect = (service) => {
    setSelectedService(service);
    setShowDetails(true);
  };

  // Calculate total based on whether user has membership or not
  const calculateTotal = () => {
    if (!selectedService) return 0;

    // If user has active membership, only charge application fee
    if (hasMembership && canUseForApplication()) {
      return selectedService.application_fee;
    }

    // Otherwise charge full amount
    const subtotal = selectedService.application_fee + selectedService.service_fee;
    const taxAmount = (subtotal * selectedService.taxes) / 100;
    return subtotal + taxAmount;
  };

  const handleProceed = async () => {
    if (!selectedService) {
      toast.error('Please select a service');
      return;
    }

    // Determine if membership should be used
    const useMembership = hasMembership && canUseForApplication();

    const orderData = {
      serviceName: selectedService.name,
      serviceId: selectedService.id,
      applicationFee: selectedService.application_fee,
      serviceFee: useMembership ? 0 : selectedService.service_fee,
      taxes: useMembership ? 0 : (selectedService.application_fee + selectedService.service_fee) * selectedService.taxes / 100,
      totalAmount: calculateTotal(),
      additionalContacts,
      documents: selectedService.required_documents,
      processingTime: selectedService.processing_time,
      useMembership: useMembership,
      membershipId: useMembership ? membershipDetails.plan.id : null,
      serviceType: serviceType // Add service type to order data
    };

    const created = await createOrder(orderData);
    
    // Route based on service type
    if (!created) return; // stop if creation failed
    if (serviceType === 'home') {
      // Skip document upload for home services, go directly to payment
      navigate('/payment');
    } else {
      // Online services require document upload
      navigate('/upload-documents');
    }
  };

  const handleBackToServiceType = () => {
    navigate('/service-type-selection');
  };

  const getServiceTypeInfo = () => {
    if (serviceType === 'home') {
      return {
        title: 'Home Services',
        description: 'Professional operators will visit your home',
        icon: FiHome,
        color: 'from-blue-500 to-blue-600',
        bgColor: 'bg-blue-50',
        borderColor: 'border-blue-200'
      };
    } else {
      return {
        title: 'Online Services',
        description: 'Complete your application online with document upload',
        icon: FiMonitor,
        color: 'from-green-500 to-green-600',
        bgColor: 'bg-green-50',
        borderColor: 'border-green-200'
      };
    }
  };

  const serviceTypeInfo = getServiceTypeInfo();

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Service Type Banner */}
        <motion.div
          initial={{opacity: 0, y: 20}}
          animate={{opacity: 1, y: 0}}
          transition={{duration: 0.6}}
          className="mb-6"
        >
          <Card className={`p-4 ${serviceTypeInfo.bgColor} ${serviceTypeInfo.borderColor} border`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className={`bg-gradient-to-r ${serviceTypeInfo.color} text-white p-3 rounded-lg`}>
                  <SafeIcon icon={serviceTypeInfo.icon} className="w-6 h-6" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">{serviceTypeInfo.title}</h2>
                  <p className="text-sm text-gray-600">{serviceTypeInfo.description}</p>
                </div>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={handleBackToServiceType}
                className="text-gray-600 border-gray-300 hover:bg-gray-50"
              >
                <SafeIcon icon={FiArrowLeft} className="w-4 h-4 mr-2" />
                Change Service Type
              </Button>
            </div>
          </Card>
        </motion.div>

        <motion.div
          initial={{opacity: 0, y: 20}}
          animate={{opacity: 1, y: 0}}
          transition={{duration: 0.6}}
          className="text-center mb-8"
        >
          <h1 className="text-3xl font-bold text-gray-900">Select Your Service</h1>
          <p className="text-gray-600 mt-2">Choose the application service you need</p>
        </motion.div>

        {/* Search Bar */}
        <div className="mb-8">
          <div className="relative max-w-md mx-auto">
            <SafeIcon icon={FiSearch} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search services..."
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
        </div>

        {/* Membership Banner (if active) */}
        {hasMembership && (
          <motion.div
            initial={{opacity: 0, y: 20}}
            animate={{opacity: 1, y: 0}}
            transition={{duration: 0.6}}
            className="mb-8"
          >
            <Card className="p-4 bg-gradient-to-r from-primary-50 to-blue-50 border-primary-200">
              <div className="flex items-center">
                <div className="p-3 bg-primary-100 rounded-full">
                  <SafeIcon icon={FiStar} className="w-6 h-6 text-primary-600" />
                </div>
                <div className="ml-4">
                  <h3 className="font-semibold text-gray-900">Active Membership: {membershipDetails?.plan?.name}</h3>
                  <p className="text-gray-700">
                    {membershipDetails?.applicationsLimit === null
                      ? 'Unlimited applications available'
                      : `${membershipDetails?.remainingApplications} of ${membershipDetails?.applicationsLimit} applications remaining`}
                  </p>
                  <p className="text-sm text-primary-700 mt-1">
                    With your membership, you'll only pay the application fee, no service charges!
                  </p>
                </div>
              </div>
            </Card>
          </motion.div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Services Grid */}
          <div className="lg:col-span-2">
            {loading ? (
              <div className="flex justify-center items-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-600"></div>
              </div>
            ) : filteredServices.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {filteredServices.map((service, index) => (
                  <motion.div
                    key={service.id}
                    initial={{opacity: 0, y: 20}}
                    animate={{opacity: 1, y: 0}}
                    transition={{duration: 0.5, delay: index * 0.1}}
                    onClick={() => handleServiceSelect(service)}
                    className="cursor-pointer"
                  >
                    <Card className={`p-6 transition-all duration-300 hover:shadow-lg ${selectedService?.id === service.id ? 'ring-2 ring-primary-500 shadow-lg' : ''}`}>
                      <div className={`bg-gradient-to-r ${service.color} text-white p-4 rounded-lg mb-4`}>
                        <SafeIcon icon={service.iconComponent} className="w-8 h-8 mb-2" />
                        <h3 className="text-lg font-semibold">{service.name}</h3>
                      </div>
                      <p className="text-gray-600 mb-4">{service.description}</p>
                      <div className="space-y-2">
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">Application Fee:</span>
                          <span className="font-semibold">₹{service.application_fee}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">Service Fee:</span>
                          <span className="font-semibold">
                            {hasMembership && canUseForApplication() ? (
                              <span className="line-through text-gray-400">₹{service.service_fee}</span>
                            ) : (
                              `₹${service.service_fee}`
                            )}
                            {hasMembership && canUseForApplication() && (
                              <span className="ml-2 text-green-600">FREE</span>
                            )}
                          </span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">Processing Time:</span>
                          <span className="font-semibold">{service.processing_time}</span>
                        </div>
                      </div>
                    </Card>
                  </motion.div>
                ))}
              </div>
            ) : (
              <Card className="p-12 text-center">
                <SafeIcon icon={FiSearch} className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">No services found</h3>
                <p className="text-gray-600">Try adjusting your search keywords</p>
              </Card>
            )}
          </div>

          {/* Service Details Sidebar */}
          <div className="space-y-6">
            {selectedService && showDetails && (
              <motion.div
                initial={{opacity: 0, x: 20}}
                animate={{opacity: 1, x: 0}}
                transition={{duration: 0.5}}
              >
                <Card className="p-6">
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">Service Details</h3>
                  
                  {/* Fee Breakdown */}
                  <div className="mb-6">
                    <h4 className="font-medium text-gray-900 mb-3">Fee Breakdown</h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span>Application Fee:</span>
                        <span>₹{selectedService.application_fee}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Service Fee:</span>
                        {hasMembership && canUseForApplication() ? (
                          <span>
                            <span className="line-through text-gray-400">₹{selectedService.service_fee}</span>
                            <span className="ml-2 text-green-600">₹0</span>
                          </span>
                        ) : (
                          <span>₹{selectedService.service_fee}</span>
                        )}
                      </div>
                      {!hasMembership || !canUseForApplication() ? (
                        <div className="flex justify-between">
                          <span>Taxes ({selectedService.taxes}%):</span>
                          <span>
                            ₹{((selectedService.application_fee + selectedService.service_fee) * selectedService.taxes / 100).toFixed(2)}
                          </span>
                        </div>
                      ) : null}
                      <div className="border-t pt-2 flex justify-between font-semibold">
                        <span>Total Amount:</span>
                        <span>₹{calculateTotal().toFixed(2)}</span>
                      </div>
                    </div>

                    {/* Membership Savings Banner */}
                    {hasMembership && canUseForApplication() && (
                      <div className="mt-3 bg-green-50 p-2 rounded-lg border border-green-200">
                        <div className="flex items-center">
                          <SafeIcon icon={FiStar} className="w-4 h-4 text-green-600 mr-2" />
                          <p className="text-sm text-green-800">
                            You save ₹{" "}
                            {(
                              selectedService.service_fee +
                              (selectedService.application_fee + selectedService.service_fee) * selectedService.taxes / 100
                            ).toFixed(2)}{" "}
                            with your membership!
                          </p>
                        </div>
                      </div>
                    )}

                    {/* Membership Promotion */}
                    {(!hasMembership || !canUseForApplication()) && (
                      <div className="mt-3 bg-blue-50 p-3 rounded-lg border border-blue-200">
                        <div className="flex items-center">
                          <SafeIcon icon={FiStar} className="w-5 h-5 text-blue-600 mr-2" />
                          <div>
                            <p className="text-sm font-medium text-blue-800">Save with a Membership!</p>
                            <p className="text-xs text-blue-700">
                              Get a membership and pay only the application fee, no service charges or taxes!
                            </p>
                          </div>
                        </div>
                        <div className="mt-2 text-center">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => navigate('/membership')}
                            className="text-blue-700 border-blue-300 hover:bg-blue-50"
                          >
                            View Membership Plans
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Service Type Specific Information */}
                  <div className="mb-6">
                    <h4 className="font-medium text-gray-900 mb-3">Service Type: {serviceTypeInfo.title}</h4>
                    <div className={`${serviceTypeInfo.bgColor} p-3 rounded-lg border ${serviceTypeInfo.borderColor}`}>
                      <div className="flex items-start space-x-2">
                        <SafeIcon icon={serviceTypeInfo.icon} className="w-5 h-5 text-gray-600 mt-0.5" />
                        <div>
                          <p className="text-sm font-medium text-gray-900">{serviceTypeInfo.title}</p>
                          <p className="text-xs text-gray-600">{serviceTypeInfo.description}</p>
                          {serviceType === 'home' && (
                            <p className="text-xs text-blue-600 mt-1">✓ No document upload required</p>
                          )}
                          {serviceType === 'online' && (
                            <p className="text-xs text-green-600 mt-1">✓ Document upload required</p>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Required Documents */}
                  <div className="mb-6">
                    <h4 className="font-medium text-gray-900 mb-3">Required Documents</h4>
                    <ul className="space-y-1 text-sm">
                      {selectedService.required_documents.map((doc, index) => (
                        <li key={index} className="flex items-center space-x-2">
                          <SafeIcon icon={FiIcons.FiFileText} className="w-4 h-4 text-gray-500" />
                          <span>{doc}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Processing Time */}
                  <div className="mb-6">
                    <div className="flex items-center space-x-2 mb-2">
                      <SafeIcon icon={FiIcons.FiClock} className="w-5 h-5 text-blue-600" />
                      <h4 className="font-medium text-gray-900">Processing Time</h4>
                    </div>
                    <p className="text-sm text-gray-600">{selectedService.processing_time}</p>
                    <p className="text-xs text-gray-500 mt-1">
                      Orders placed after 10 PM will be processed next morning from 7 AM
                    </p>
                  </div>

                  {/* Payment Modes */}
                  <div className="mb-6">
                    <h4 className="font-medium text-gray-900 mb-3">Payment Options</h4>
                    <ul className="space-y-1 text-sm">
                      <li className="flex items-center space-x-2">
                        <SafeIcon icon={FiIcons.FiDollarSign} className="w-4 h-4 text-green-500" />
                        <span>UPI (PhonePe, Google Pay, Paytm, etc.)</span>
                      </li>
                    </ul>
                  </div>

                  {/* Additional Contacts */}
                  <div className="mb-6">
                    <h4 className="font-medium text-gray-900 mb-3">Additional Contacts (Optional)</h4>
                    <div className="space-y-3">
                      <Input
                        label="Additional Mobile Number"
                        type="tel"
                        value={additionalContacts.mobile}
                        onChange={(e) => setAdditionalContacts((prev) => ({...prev, mobile: e.target.value.replace(/\D/g, '').slice(0, 10)}))}
                        placeholder="10-digit mobile number"
                      />
                      <Input
                        label="Additional Email"
                        type="email"
                        value={additionalContacts.email}
                        onChange={(e) => setAdditionalContacts((prev) => ({...prev, email: e.target.value}))}
                        placeholder="additional@email.com"
                      />
                    </div>
                  </div>

                  <Button onClick={handleProceed} className="w-full" size="lg">
                    {serviceType === 'home' ? 'Proceed to Payment' : 'Proceed to Upload Documents'}
                  </Button>
                </Card>
              </motion.div>
            )}

            {!selectedService && (
              <Card className="p-6 text-center">
                <SafeIcon icon={FiIcons.FiFileText} className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">Select a Service</h3>
                <p className="text-gray-600">Choose a service from the left to see details and pricing</p>
              </Card>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ServiceSelection;